import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();
const RESOURCE = "/imports";

export type ImportEntityType = "clients" | "services" | "products";
export type DuplicateStrategy = "skip" | "update" | "create";
export type ImportSourceLayout =
  | "unknown"
  | "adatis_clients"
  | "adatis_services"
  | "adatis_products"
  | "trinks_clients"
  | "trinks_services"
  | "trinks_products"
  | "clinicorp_clients"
  | "clinicorp_services"
  | "clinicorp_products"
  | "relinq_clients"
  | "relinq_services"
  | "relinq_products";

export type ImportAvailableLayout = {
  key: ImportSourceLayout;
  family: string;
  label: string;
};

export type ImportSessionStatus =
  | "analyzing"
  | "awaiting_confirmation"
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "undone";

export type ImportWarning = {
  row: number;
  message: string;
};

export type ImportAnalyzeData = {
  session_id: number;
  entity_type: ImportEntityType;
  source_layout: ImportSourceLayout;
  layout_confidence: number;
  layout_family: string | null;
  layout_banner: string | null;
  requires_manual_mapping: boolean;
  headers: string[];
  suggested_mapping: Record<string, string>;
  confidence: number;
  preview_rows: Record<string, unknown>[];
  warnings: ImportWarning[];
  total_rows: number;
  destination_fields: string[];
  required_fields: string[];
  duplicate_strategy: DuplicateStrategy;
  unmatched_headers: string[];
  available_layouts?: ImportAvailableLayout[];
  currency_code: string;
  locale: string;
};

export type ImportSessionStatusData = {
  session_id: number;
  entity_type: ImportEntityType;
  source_layout?: ImportSourceLayout | null;
  layout_confidence?: number | null;
  requires_manual_mapping?: boolean;
  status: ImportSessionStatus;
  original_filename: string;
  column_mapping: Record<string, string> | null;
  duplicate_strategy: DuplicateStrategy;
  total_rows: number;
  processed_rows: number;
  progress_percent: number;
  success_count: number;
  failure_count: number;
  error_summary: {
    sample?: Array<{ row: number | null; message: string }>;
    warnings?: ImportWarning[];
    skipped_count?: number;
  } | null;
  preview_rows?: Record<string, unknown>[];
  started_at: string | null;
  completed_at: string | null;
  undone_at: string | null;
  errors_download_available: boolean;
  can_undo: boolean;
  currency_code: string;
  locale: string;
  available_layouts?: ImportAvailableLayout[];
  layout_banner?: string | null;
  deleted_count?: number;
  restored_count?: number;
};

export const analyzeImport = (
  file: File,
  entityType: ImportEntityType
): Promise<ApiResponse<ImportAnalyzeData>> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("entity_type", entityType);

  return axios.post(`${RESOURCE}/analyze`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }) as Promise<ApiResponse<ImportAnalyzeData>>;
};

export const confirmImportMapping = (
  sessionId: number,
  columnMapping: Record<string, string> | null,
  duplicateStrategy: DuplicateStrategy,
  sourceLayout?: ImportSourceLayout | null
): Promise<ApiResponse<ImportSessionStatusData>> => {
  const payload: Record<string, unknown> = {
    duplicate_strategy: duplicateStrategy,
  };

  if (columnMapping !== null) {
    payload.column_mapping = columnMapping;
  }

  if (sourceLayout) {
    payload.source_layout = sourceLayout;
  }

  return api.update(
    `${RESOURCE}/${sessionId}`,
    payload
  ) as Promise<ApiResponse<ImportSessionStatusData>>;
};

export const executeImport = (
  sessionId: number
): Promise<ApiResponse<ImportSessionStatusData>> =>
  api.create(
    `${RESOURCE}/${sessionId}/execute`,
    {}
  ) as Promise<ApiResponse<ImportSessionStatusData>>;

export const undoImport = (
  sessionId: number
): Promise<ApiResponse<ImportSessionStatusData>> =>
  api.create(
    `${RESOURCE}/${sessionId}/undo`,
    {}
  ) as Promise<ApiResponse<ImportSessionStatusData>>;

export const getImportSession = (
  sessionId: number
): Promise<ApiResponse<ImportSessionStatusData>> =>
  api.get(`${RESOURCE}/${sessionId}`) as Promise<ApiResponse<ImportSessionStatusData>>;

const asDownloadBlob = (payload: unknown): Blob => {
  // Axios response interceptor already unwraps `response.data`.
  if (payload instanceof Blob) {
    return payload;
  }

  if (typeof payload === "string") {
    return new Blob([payload], { type: "text/csv;charset=utf-8" });
  }

  throw new Error("Invalid download payload");
};

export const downloadImportTemplate = async (
  entityType: ImportEntityType
): Promise<Blob> => {
  const payload = await axios.get(`${RESOURCE}/templates/${entityType}`, {
    responseType: "blob",
  });

  return asDownloadBlob(payload);
};

export const downloadImportErrors = async (sessionId: number): Promise<Blob> => {
  const payload = await axios.get(`${RESOURCE}/${sessionId}/errors`, {
    responseType: "blob",
  });

  return asDownloadBlob(payload);
};
